import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, IconButton, LinearProgress, MenuItem, Paper, Stack, TextField, Tooltip, Typography, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    createLibraryItem, newLibraryRevision, removeLibraryFile, submitLibraryItem, updateLibraryItem, uploadLibraryFile
} from './apiLibrary';
import { BRAND, FILE_KIND_LABELS, formatSize, previewCode } from './libraryConstants';
import StatusChip from './StatusChip';

const EMPTY = { deviceType: '', manufacturer: '', model: '', variant: '', orderingCode: '', title: '', description: '', tags: [] };
const FIELD_NAMES = Object.keys(EMPTY);
const IDENTITY = ['deviceType', 'manufacturer', 'model', 'variant'];

const SLOTS = [
    { kind: 'footprint_dwg', hint: 'Required before submitting' },
    { kind: 'preview_image', hint: 'Strongly recommended: shown in search results' },
    { kind: 'footprint_pdf', hint: 'Optional' },
    { kind: 'schematic_dwg', hint: 'Optional' },
    { kind: 'other', hint: 'Optional: datasheets, workbooks, archives' }
];

const fieldsFrom = (item) => (item ? Object.fromEntries(FIELD_NAMES.map(k => [k, item[k] ?? EMPTY[k]])) : { ...EMPTY });

// Create or edit a draft. item = null for a new device.
const LibraryItemForm = ({ groupId, config, item: initialItem, onClose, onSaved, onOpenExisting, onHelp }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [item, setItem] = useState(initialItem);
    const [fields, setFields] = useState(fieldsFrom(initialItem));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [duplicate, setDuplicate] = useState(null);
    const [uploads, setUploads] = useState({}); // kind -> { progress, error, name }
    const inputs = useRef({});

    useEffect(() => {
        setItem(initialItem);
        setFields(fieldsFrom(initialItem));
        setError('');
        setDuplicate(null);
        setUploads({});
    }, [initialItem]);

    const set = (k) => (e) => setFields(f => ({ ...f, [k]: e.target.value }));
    const saved = fieldsFrom(item);
    const dirty = FIELD_NAMES.some(k => JSON.stringify(fields[k]) !== JSON.stringify(saved[k]));
    const isRevision = Boolean(item && item.supersedes);
    const perms = (item && item.permissions) || {};
    const code = previewCode(fields);
    const missing = ['deviceType', 'manufacturer', 'model'].filter(k => !String(fields[k]).trim());

    // Returns the saved item, or null after showing an error
    const save = async () => {
        if (missing.length) { setError('Device type, manufacturer and model are required.'); return null; }
        setSaving(true);
        setError('');
        setDuplicate(null);
        const body = { ...fields, tags: fields.tags };
        if (isRevision) IDENTITY.forEach(k => delete body[k]);
        const res = item ? await updateLibraryItem(groupId, item._id, body) : await createLibraryItem(groupId, body);
        setSaving(false);
        if (res.error) {
            setError(res.error);
            if (res.existing) setDuplicate(res.existing);
            return null;
        }
        setItem(res);
        setFields(fieldsFrom(res));
        onSaved(res);
        return res;
    };

    const saveAndSubmit = async () => {
        let current = item;
        if (!current || dirty) current = await save();
        if (!current) return;
        if (!current.files.some(f => f.kind === 'footprint_dwg')) {
            setError('Upload a footprint DWG before submitting.');
            return;
        }
        setSaving(true);
        const res = await submitLibraryItem(groupId, current._id);
        setSaving(false);
        if (res.error) { setError(res.error); return; }
        onSaved(res);
        onClose();
    };

    const startRevision = async () => {
        setSaving(true);
        const res = await newLibraryRevision(groupId, duplicate._id);
        setSaving(false);
        if (res.error) { setError(res.error); return; }
        setDuplicate(null);
        setError('');
        setItem(res);
        setFields(fieldsFrom(res));
        onSaved(res);
    };

    const pickFile = async (kind, file) => {
        if (!file || !item) return;
        const ext = `.${file.name.split('.').pop().toLowerCase()}`;
        const allowed = config.taxonomy.fileKinds[kind] || [];
        if (!allowed.includes(ext)) {
            setUploads(u => ({ ...u, [kind]: { error: `${FILE_KIND_LABELS[kind]} must be ${allowed.join(', ')}` } }));
            return;
        }
        if (file.size > config.maxFileSize) {
            setUploads(u => ({ ...u, [kind]: { error: `Files must be under ${formatSize(config.maxFileSize)}` } }));
            return;
        }
        setUploads(u => ({ ...u, [kind]: { progress: 0, name: file.name } }));
        const res = await uploadLibraryFile(groupId, item._id, kind, file,
            (progress) => setUploads(u => ({ ...u, [kind]: { ...u[kind], progress } })));
        if (res.error) {
            setUploads(u => ({ ...u, [kind]: { error: res.error } }));
            return;
        }
        setUploads(u => ({ ...u, [kind]: undefined }));
        setItem(res);
        onSaved(res);
    };

    const removeFile = async (file) => {
        const res = await removeLibraryFile(groupId, item._id, file._id);
        if (res.error) { setError(res.error); return; }
        setItem(res);
        onSaved(res);
    };

    const busyUploading = Object.values(uploads).some(u => u && u.progress !== undefined);

    return (
        <Dialog open onClose={() => !saving && !busyUploading && onClose()} fullScreen={fullScreen} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, width: '100%' }}>
                    <span>{item ? (isRevision ? `New revision r${item.revision}` : 'Edit device') : 'Add a device'}</span>
                    {item && <StatusChip status={item.status} />}
                    {onHelp && (
                        <Button size="small" startIcon={<HelpOutlineIcon />} onClick={onHelp} sx={{ ml: 'auto !important', textTransform: 'none' }}>
                            How to add a device
                        </Button>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, bgcolor: '#F5F8FC', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Library code</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primary, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {code || '—'}{item ? ` · r${item.revision}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Built from device type, manufacturer, model and variant. Downloads use this name, e.g. {code || 'PR_SEL_351S7'}.dwg
                    </Typography>
                </Paper>

                {isRevision && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This is a new revision, so its device identity is fixed. Upload the updated files below; files from the previous revision are not copied.
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth required label="Device type" value={fields.deviceType} onChange={set('deviceType')} disabled={isRevision}>
                            {config.taxonomy.deviceTypes.map(t => <MenuItem key={t.code} value={t.code}>{t.label} ({t.code})</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth required label="Manufacturer" value={fields.manufacturer} onChange={set('manufacturer')} disabled={isRevision}>
                            {config.taxonomy.manufacturers.map(m => <MenuItem key={m.code} value={m.code}>{m.label} ({m.code})</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Model" placeholder="351S7" value={fields.model} onChange={set('model')}
                            disabled={isRevision} inputProps={{ maxLength: 100 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Variant" placeholder="e.g. WITHOUT-PUSHBUTTONS" value={fields.variant} onChange={set('variant')}
                            disabled={isRevision} inputProps={{ maxLength: 100 }} helperText="Only if this differs from the base model" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Ordering code" placeholder="Full manufacturer part number" value={fields.orderingCode}
                            onChange={set('orderingCode')} inputProps={{ maxLength: 200 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Title" placeholder="SEL-351S7 Protection System" value={fields.title}
                            onChange={set('title')} inputProps={{ maxLength: 200 }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={3} label="Description" value={fields.description}
                            onChange={set('description')} inputProps={{ maxLength: 4000 }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={fields.tags}
                            onChange={(e, value) => setFields(f => ({
                                ...f,
                                tags: [...new Set(value.map(v => String(v).trim().toLowerCase()).filter(Boolean))].slice(0, 20)
                            }))}
                            renderTags={(value, getTagProps) => value.map((tag, i) => <Chip size="small" label={tag} {...getTagProps({ index: i })} key={tag} />)}
                            renderInput={(params) => <TextField {...params} label="Tags" placeholder="Type a tag and press Enter" />}
                        />
                    </Grid>
                </Grid>

                {error && (
                    <Alert severity={duplicate ? 'warning' : 'error'} sx={{ mt: 2 }}
                        action={duplicate && (duplicate.status === 'published' ? (
                            <Button color="inherit" size="small" onClick={startRevision} disabled={saving}>Create new revision</Button>
                        ) : (
                            <Button color="inherit" size="small" onClick={() => onOpenExisting(duplicate._id)}>Open it</Button>
                        ))}>
                        {error}{duplicate && duplicate.status === 'published' ? '. Create a new revision instead?' : ''}
                    </Alert>
                )}

                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primary, mt: 3, mb: 1 }}>Files</Typography>
                {!item && <Alert severity="info" sx={{ mb: 1 }}>Save the draft to attach files.</Alert>}
                <Stack spacing={1.5}>
                    {SLOTS.map(({ kind, hint }) => {
                        const files = item ? item.files.filter(f => f.kind === kind) : [];
                        const single = config.taxonomy.singletonKinds.includes(kind);
                        const up = uploads[kind];
                        const accept = (config.taxonomy.fileKinds[kind] || []).join(',');
                        return (
                            <Paper key={kind} variant="outlined" sx={{ p: 1.5, borderRadius: 2, opacity: item ? 1 : 0.6 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {FILE_KIND_LABELS[kind]}{kind === 'footprint_dwg' ? ' *' : ''}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{hint} · {accept}</Typography>
                                    </Box>
                                    <Box>
                                        <input
                                            type="file"
                                            hidden
                                            accept={accept}
                                            ref={el => { inputs.current[kind] = el; }}
                                            onChange={e => { pickFile(kind, e.target.files[0]); e.target.value = ''; }}
                                        />
                                        <Button size="small" variant="outlined" startIcon={<UploadFileIcon />}
                                            disabled={!item || !perms.canUpload || Boolean(up && up.progress !== undefined)}
                                            onClick={() => inputs.current[kind] && inputs.current[kind].click()}>
                                            {single && files.length ? 'Replace' : 'Upload'}
                                        </Button>
                                    </Box>
                                </Stack>
                                {files.map(f => (
                                    <Stack key={f._id} direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {f.originalName} <Typography component="span" variant="caption" color="text.secondary">· {formatSize(f.fileSize)}</Typography>
                                        </Typography>
                                        {perms.canRemoveFiles && (
                                            <Tooltip title="Remove file">
                                                <IconButton size="small" aria-label={`Remove ${f.originalName}`} onClick={() => removeFile(f)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                ))}
                                {up && up.progress !== undefined && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption">{up.name} · {up.progress}%</Typography>
                                        <LinearProgress variant="determinate" value={up.progress} />
                                    </Box>
                                )}
                                {up && up.error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploads(u => ({ ...u, [kind]: undefined }))}>{up.error}</Alert>}
                            </Paper>
                        );
                    })}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Button onClick={onClose} disabled={saving || busyUploading}>Close</Button>
                <Button variant="outlined" onClick={save} disabled={saving || busyUploading || (item && !dirty)}
                    startIcon={saving ? <CircularProgress size={16} /> : null}>
                    {item ? 'Save changes' : 'Save draft'}
                </Button>
                {(!item || perms.canSubmit) && (
                    <Button variant="contained" onClick={saveAndSubmit} disabled={saving || busyUploading || !item}
                        sx={{ bgcolor: BRAND.action, '&:hover': { bgcolor: BRAND.actionHover } }}>
                        Submit for review
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default LibraryItemForm;
